import React, {useState, useEffect, useRef} from 'react';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dialog } from 'primereact/dialog';
import { FilterMatchMode, FilterOperator } from 'primereact/api';
import { parseDateTime } from '../../../common/date-time';

import { useSelector, useDispatch } from 'react-redux';
import { getNacAutomationDetailsByRitmInit, getNacAutomatonEventsByRitmInit} from './redux/actions';
import './DetailSummaryDialog.scss';


const DetailSummaryDialog = ({dialogData, setDialogData}) => {

  const nacAutomationDetails = useSelector((state) => state.nacAutomation.nacAutomationDetailsbyRitm);
  const nacAutomationEvents = useSelector((state) => state.nacAutomation.nacAutomationEventsbyRitm);

    const [selectedId, setSelectedId] = useState(null);
    const [tableFilters, setTableFilters] = useState(null);
    const detailsSummaryTableRef = useRef(null);
    const dispatch = useDispatch();

    useEffect(() => {
        initializeTableFilter();
    }, []);

    useEffect(() => {
        if (!!dialogData) {
            fetchDetails();
            setSelectedId(null);
        }
    },[dialogData]);

    const fetchDetails = () => dispatch(getNacAutomationDetailsByRitmInit(dialogData));
    const fetchEvents = (requestId, switchName, portNumber) => {
        setSelectedId(requestId);
        dispatch(getNacAutomatonEventsByRitmInit(requestId, switchName, portNumber));
    }

      /**
   * Function to initialize Table filter
   */
  const initializeTableFilter = () => {
    setTableFilters({
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
      requestId: {
        operator: FilterOperator.AND,
        constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
      },
      storeCode: {
        operator: FilterOperator.AND,
        constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
      },
      division: {
        operator: FilterOperator.AND,
        constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
      },
      switchName: {
        operator: FilterOperator.AND,
        constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
      },
      serial: {
        operator: FilterOperator.AND,
        constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
      },
      portNumber: {
        operator: FilterOperator.AND,
        constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
      },
      policyType: {
        operator: FilterOperator.AND,
        constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
      },
      policyName: {
        operator: FilterOperator.AND,
        constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
      },
      policyNumber: {
        operator: FilterOperator.AND,
        constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
      },
      status: {
        operator: FilterOperator.AND,
        constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
      },
    });
  };

    const exportDetails = () => detailsSummaryTableRef.current.exportCSV();

    const createdDateTemplate = ({createdDate}) => (
      !!createdDate ? parseDateTime(createdDate) : <span>-</span>
    );

    const modifiedDateTemplate = ({modifiedDate}) => (
      !!modifiedDate ? parseDateTime(modifiedDate) : <span>-</span>
    );

    const storeCodeTemplate = ({storeCode}) => (
      !!storeCode ? storeCode : <span>-</span>
    );

    const divisionTemplate = ({division}) => (
      !!division ? division : <span>-</span>
    );

    const switchNameTemplate = ({switchName}) => (
      !!switchName ? switchName : <span>-</span>
    );

    const serialNumberTemplate = ({serial}) => (
      !!serial ? serial : <span>-</span>
    );

    const portNumberTemplate = ({portNumber}) => (
      !!portNumber ? portNumber : <span>-</span>
    );

    const policyTypeTemplate = ({policyType}) => (
      !!policyType ? policyType : <span>-</span>
    );

    const policyNameTemplate = ({policyName}) => (
      !!policyName ? policyName : <span>-</span>
    );

    const policyNumberTemplate = ({policyNumber}) => (
      !!policyNumber ? policyNumber : <span>-</span>
    );

    const eventsTemplate = ({requestId, switchName, portNumber}) => (
        <i className="pi pi-eye" onClick={() => fetchEvents(requestId, switchName, portNumber)}/>
    );

    const timeStampTemplate = ({timestamp}) => (
      !!timestamp ? parseDateTime(timestamp) : <span>-</span>
    );

    return (
        <Dialog
        className="nac-automation-detail-summary-dialog"
        visible={!!dialogData}
        onHide={() => setDialogData(null)}
        header={!!selectedId ? selectedId : dialogData}
        >
            {!!selectedId ? (
                <>
                    <Button className="p-button-text back-icon" icon="pi pi-arrow-left" onClick={() => setSelectedId(null)}/>
                    <DataTable 
                    value={nacAutomationEvents} 
                    key="nac-automation-events-table"
                    sortField='timestamp'
                    sortOrder={-1}
                    emptyMessage="No events found" 
                    >
                        <Column field="events" header="Event" sortable filter />
                        <Column field="logMessage" header="Message" sortable filter />
                        <Column field="timestamp" header="Timestamp" body={timeStampTemplate} sortable />
                    </DataTable>
                </>
            ) : (
                <>
                <div className='d-flex justify-content-end'>
                <Button className='p-button-text' icon="pi pi-refresh" onClick={() => fetchDetails()}/>
                <Button className='p-button-text' icon="pi pi-download" onClick={() => exportDetails()}/>
                </div>
                <DataTable
                value={nacAutomationDetails}
                ref={detailsSummaryTableRef}
                key="nac-automation-details-table"
                className="nac-automation-details-table"
                filters={tableFilters}
                filterDisplay='menu'
                sortField="createdDate"
                sortOrder={-1}
                emptyMessage="No Details found."
                >
                    <Column field="requestId" header="RITM" sortable filter />
                    <Column field="storeCode" header="Store" body={storeCodeTemplate} sortable filter />
                    <Column field="division" header="Divison" body={divisionTemplate}sortable filter />
                    <Column field="switchName" header="Switch Name" body={switchNameTemplate} sortable filter />
                    <Column field="serial" header="Serial Number" body={serialNumberTemplate} filter />
                    <Column field="portNumber" header="Port Number" body={portNumberTemplate} sortable filter />
                    <Column field="policyType" header="Policy Type" body={policyTypeTemplate} sortable filter />
                    <Column field="policyName" header="Policy Name" body={policyNameTemplate} sortable filter />
                    <Column field="policyNumber" header="Policy Number" body={policyNumberTemplate} sortable filter />
                    <Column field="status" header="Status" sortable filter />
                    <Column field="createdDate" header="Created Date" body={createdDateTemplate} sortable/>
                    <Column field="modifiedDate" header="Modified Date" body={modifiedDateTemplate} sortable/>
                    <Column field="event" header="Events" body={eventsTemplate}/>
                </DataTable>
                </>
            )}
        </Dialog>
    )
}

export default DetailSummaryDialog;